import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Success.css";
import AppBar from "../../components/AppBar";
import Footer from "../../components/Footer";
import { AuthContext } from "../../context/AuthContext";
import {
  getPlanPrice,
  getBlockchainPrice,
  getTotalPrice,
} from "../../utils/pricing";


export default function Success() {
  // Pull auth state directly from AuthContext
  const { user, loading } = useContext(AuthContext);

  const [showPopup, setShowPopup] = useState(false);
  const navigate = useNavigate();

  /* -------------------------
     AUTH / STATE GUARDS
  --------------------------*/

  if (loading) {
    return (
      <div>
        <AppBar />
        <div style={{ height: "50px" }} />
        <div className="success-container">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  // Not logged in
  if (!user) {
    return (
      <div>
        <AppBar />
        <div style={{ height: "50px" }} />
        <div className="success-container">
          <h1>Not Logged In</h1>
          <p className="subtitle">Please log in to continue</p>

          <div className="order-details-container">
            <div className="details-section">
              <p className="error-message">
                You need to be logged in to access this page.
              </p>
              <button
                className="try-again-button"
                onClick={() => navigate("/login")}
              >
                Log In
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Logged in but no active plan
  if (user.status !== "active" || !user.planId) {
    return (
      <div>
        <AppBar />
        <div style={{ height: "50px" }} />
        <div className="success-container">
          <h2>Oops!</h2>
          <p>No active order found on this account.</p>
        </div>
      </div>
    );
  }

  /* -------------------------
     DATA FROM VERIFY-TOKEN
  --------------------------*/

  const purchaseData = user;

  const basePrice = getPlanPrice(purchaseData.planId);
  const blockchainPrice = getBlockchainPrice(
    purchaseData.planId,
    purchaseData.blockchains
  );
  const total = getTotalPrice(
    purchaseData.planId,
    purchaseData.blockchains
  );


  /* -------------------------
     FORMATTERS
  --------------------------*/

  const formatDate = (timestamp) => {
    if (!timestamp?._seconds) return "N/A";
    return new Date(timestamp._seconds * 1000).toLocaleDateString();
  };

  const formatBlockchains = (blockchains) => {
    if (!Array.isArray(blockchains)) return "N/A";
    return blockchains
      .map(c => c.charAt(0).toUpperCase() + c.slice(1))
      .join(", ");
  };

  const capitalizeFirstLetter = (str) => {
    if (!str) return "N/A";
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const getPlanExpiryDisplay = () => {
    if (purchaseData.planId === "custom") {
      return formatDate(purchaseData.plan_expires);
    }
    return "Lifetime";
  };

  /* -------------------------
     DOWNLOAD HANDLING
  --------------------------*/

  const handleDownloadClick = (e) => {
    if (!purchaseData.url_expires?._seconds) {
      e.preventDefault();
      setShowPopup(true);
      return;
    }

    const expiresAt = new Date(purchaseData.url_expires._seconds * 1000);
    if (expiresAt < new Date()) {
      e.preventDefault();
      setShowPopup(true);
    }
  };

  /* -------------------------
     RENDER
  --------------------------*/

  return (
    <div>
      <AppBar />
      <div style={{ height: "140px" }} />

      {showPopup && (
        <div
          className="popup-overlay"
          onClick={(e) => e.target === e.currentTarget && setShowPopup(false)}
        >
          <div className="popup">
            <button className="popup-close" onClick={() => setShowPopup(false)}>×</button>
            <p>
              Download link has expired. Please{" "}
              <span className="popup-link" onClick={() => navigate("/dashboard")}>
                navigate to the dashboard
              </span>{" "}
              to regenerate it.
            </p>
          </div>
        </div>
      )}

      <div className="success-container">
        <h1>Thank you!</h1>
        <p className="subtitle">Your order has been processed</p>

        <div className="order-details-container">
          <h2>Order Details</h2>

          <div className="details-section">
            <div className="detail-row">
              <span>Plan</span>
              <span>{capitalizeFirstLetter(purchaseData.planId)}</span>
            </div>
            <div className="detail-row">
              <span>Blockchains</span>
              <span>{formatBlockchains(purchaseData.blockchains)}</span>
            </div>
            <div className="detail-row">
              <span>Date</span>
              <span>{formatDate(purchaseData.purchased_at)}</span>
            </div>
            <div className="detail-row">
              <span>Plan Expiry Date</span>
              <span>{getPlanExpiryDisplay()}</span>
            </div>
            <div className="detail-row">
              <span>Payment Method</span>
              <span>Crypto</span>
            </div>
          </div>

          <div className="divider"></div>

          <div className="details-section">
            <div className="detail-row total">
              <span>Base Price</span>
              <span>${basePrice}</span>
            </div>
            <div className="detail-row total">
              <span>Blockchains</span>
              <span>${blockchainPrice}</span>
            </div>
            <div className="detail-row total">
              <span>Total</span>
              <span>${total}</span>
            </div>
          </div>
        </div>

        <div className="products-section">
          <h2>Products</h2>

          <div className="details-section">
            <div className="detail-row white-text">
              <span>LunaSearch Apk Android</span>
              <span>$0</span>
            </div>
            <div className="detail-row">
              <a
                href={purchaseData.downloadUrl}
                className="download-link"
                onClick={handleDownloadClick}
              >
                Download link 1
              </a>
            </div>
          </div>

          <div className="divider"></div>

          <div className="details-section">
            <div className="detail-row white-text">
              <span>License Use</span>
              <span>${total}</span>
            </div>
          </div>

          <div className="divider"></div>

          <p className="dashboard-info">
            To see further information about your current plan or re-generate your
            download link if it has expired,{" "}
            <a href="/dashboard" className="dashboard-link">
              navigate to the dashboard
            </a>
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
}
